/* ==========================================================================
   Virsaay — cart
   Drawer, cart page and quick add. Every mutation goes through the Cart AJAX
   API with the Section Rendering API riding along, so the drawer, the cart
   page and the header count are all re-rendered from Shopify's own markup.

   Mutations run one at a time through a queue and address lines by their
   key, so rapid clicks can never target a line that has since moved.
   ========================================================================== */

class VirsaayCart {
  constructor() {
    this.isOpen = false;
    this.tween = null;
    this.queue = Promise.resolve();
    this.pending = {};
    this.noteTimer = null;
    this.errorTimer = null;
    this.bindEvents();
  }

  /* -- Lookups (read live: the theme editor can swap any of these) ------ */

  get drawer() {
    return document.getElementById('VirsaayCartDrawer');
  }

  get scrim() {
    return document.querySelector('[data-scrim]');
  }

  get page() {
    return document.querySelector('[data-cart-page]');
  }

  get motion() {
    return window.VirsaayMotion || null;
  }

  get theme() {
    return window.VirsaayTheme || {};
  }

  get reduced() {
    return !window.gsap || !this.motion || this.motion.reduced;
  }

  dur(seconds) {
    return this.motion ? this.motion.dur(seconds) : seconds;
  }

  route(name, fallback) {
    const routes = this.theme.routes || {};
    return `${routes[name] || fallback}.js`;
  }

  string(name, fallback) {
    const strings = this.theme.strings || {};
    return strings[name] || fallback || '';
  }

  sectionIds() {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const page = this.page;
    if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
    return ids;
  }

  /* -- Events ------------------------------------------------------------ */

  bindEvents() {
    document.addEventListener('click', (e) => this.onClick(e));
    document.addEventListener('submit', (e) => this.onSubmit(e));
    document.addEventListener('input', (e) => this.onInput(e));
    document.addEventListener('keydown', (e) => this.onKeydown(e));
    document.addEventListener('mouseover', (e) => this.handleThumbnailHover(e));
    document.addEventListener('virsaay:cart:open', () => this.openDrawer());
    document.addEventListener('virsaay:cart:close', () => this.closeDrawer());
    document.addEventListener('virsaay:cart:updated', (e) => this.onExternalUpdate(e));
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.querySelector && e.target.querySelector('#VirsaayCartDrawer')) this.isOpen = false;
    });
  }

  onClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-cart-open]');
    if (opener) {
      if ((this.theme.cart && this.theme.cart.type === 'page') || !this.drawer) return;
      e.preventDefault();
      this.openDrawer();
      return;
    }

    if (this.isOpen && (target.closest('[data-cart-close]') || target === this.scrim)) {
      e.preventDefault();
      this.closeDrawer();
      return;
    }

    const remove = target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      this.change(remove.closest('[data-cart-item]'), 0);
      return;
    }

    const stepper = target.closest('[data-quantity-plus], [data-quantity-minus]');
    if (stepper && stepper.closest('[data-cart-item]')) {
      e.preventDefault();
      this.step(stepper);
      return;
    }

    /* Editorial card: Instant Buy Now */
    const buyNowBtn = target.closest('[data-buy-now]');
    if (buyNowBtn) {
      e.preventDefault();
      this.buyNow(buyNowBtn);
      return;
    }

    /* Editorial card: Interactive Swatch Dot */
    const swatchDot = target.closest('[data-swatch-dot]');
    if (swatchDot) {
      e.preventDefault();
      const swatchesContainer = swatchDot.closest('[data-card-swatches]');
      if (swatchesContainer) {
        swatchesContainer.querySelectorAll('[data-swatch-dot]').forEach((el) => el.classList.remove('is-active'));
        swatchDot.classList.add('is-active');
      }
      const card = swatchDot.closest('[data-card]');
      if (!card) return;

      const variantId = swatchDot.dataset.variantId;
      if (variantId) {
        const idInput = card.querySelector('[data-card-variant-id]');
        if (idInput) idInput.value = variantId;
        card.querySelectorAll('[data-buy-now], [data-quick-add]').forEach((btn) => {
          btn.dataset.variantId = variantId;
        });
      }

      const variantImg = swatchDot.dataset.variantImage;
      if (variantImg) {
        const primaryImg = card.querySelector('.vcard__image--primary');
        if (primaryImg) {
          primaryImg.src = variantImg;
          if (primaryImg.srcset) primaryImg.removeAttribute('srcset');
        }
      }
      return;
    }

    /* Editorial card: Detail Strip Thumbnails (Click) */
    const detailThumb = target.closest('[data-detail-thumb]');
    if (detailThumb) {
      e.preventDefault();
      this.selectDetailThumb(detailThumb);
    }
  }

  onSubmit(e) {
    const form = e.target.closest && e.target.closest('[data-quick-add-form]');
    if (!form) return;
    e.preventDefault();
    this.quickAdd(form, e.submitter);
  }

  onInput(e) {
    const input = e.target;
    if (!(input instanceof Element)) return;

    if (input.matches('[data-quantity-input]')) {
      const item = input.closest('[data-cart-item]');
      if (!item) return;
      const raw = input.value.trim();
      if (raw === '') return;
      let quantity = parseInt(raw, 10);
      if (!Number.isFinite(quantity) || quantity < 0) return;
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      if (quantity > max) {
        quantity = max;
        input.value = max;
        this.showError(this.quantityError(max));
      }
      this.scheduleChange(item, quantity, 500);
      return;
    }

    if (input.matches('[data-cart-note]')) {
      clearTimeout(this.noteTimer);
      const note = input.value;
      this.noteTimer = setTimeout(() => this.saveNote(note), 500);
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.closeDrawer();
      return;
    }

    // Enter inside a quantity field would otherwise submit the cart form,
    // which posts to checkout. Commit the quantity instead.
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-quantity-input]')) {
      e.preventDefault();
      const item = e.target.closest('[data-cart-item]');
      const quantity = parseInt(e.target.value, 10);
      if (item && Number.isFinite(quantity) && quantity >= 0) this.scheduleChange(item, quantity, 0);
    }
  }

  onExternalUpdate(e) {
    const detail = e.detail || {};
    if (detail.source === 'virsaay-cart') return;

    const sections = detail.sections || (detail.cart && detail.cart.sections) || null;
    let count = null;
    if (typeof detail.item_count === 'number') count = detail.item_count;
    else if (detail.cart && typeof detail.cart.item_count === 'number') count = detail.cart.item_count;

    if (sections && (sections['cart-drawer'] || sections['cart-icon-bubble'])) {
      this.render(sections, count);
    } else {
      this.refresh();
    }
  }

  /* -- Drawer ------------------------------------------------------------ */

  openDrawer() {
    const drawer = this.drawer;
    if (!drawer || this.isOpen) return;
    this.isOpen = true;

    // Two overlays never share the screen: fold away the menus first.
    const header = document.querySelector('[data-header]');
    if (header && header._virsaayDesktopMenu && header._virsaayDesktopMenu.isOpen()) {
      header._virsaayDesktopMenu.close(false);
    }
    if (window._virsaayMobileNav && window._virsaayMobileNav.isOpen()) {
      window._virsaayMobileNav.close(null, false);
    }

    const scrim = this.scrim;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.hidden = false;
    this.setExpanded(true);

    if (this.motion) {
      this.motion.lockScroll(true);
      this.motion.trapFocus(drawer, drawer.querySelector('[data-cart-close]'));
    }

    if (this.reduced) {
      if (scrim) scrim.style.opacity = '1';
    } else {
      const gsap = window.gsap;
      if (this.tween) {
        this.tween.kill();
      } else {
        gsap.set(drawer, { xPercent: 100 });
      }

      const tl = gsap.timeline();
      if (scrim) tl.to(scrim, { opacity: 1, duration: this.dur(0.35), ease: 'power2.out' }, 0);
      tl.to(drawer, { xPercent: 0, duration: this.dur(0.6), ease: this.motion.easeExpo }, 0);

      const items = drawer.querySelectorAll('.vcart-item');
      if (items.length) {
        tl.fromTo(
          items,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: this.dur(0.4), stagger: 0.05, ease: 'power2.out', clearProps: 'transform,opacity' },
          0.18
        );
      }
      tl.eventCallback('onComplete', () => {
        this.tween = null;
      });
      this.tween = tl;
    }

    if (this.motion) {
      this.motion.announce(drawer.dataset.stringOpened || '');
    }
  }

  closeDrawer() {
    const drawer = this.drawer;
    if (!drawer || !this.isOpen) return;
    this.isOpen = false;

    const scrim = this.scrim;
    this.setExpanded(false);
    if (this.motion) {
      this.motion.releaseFocus(true, drawer);
      this.motion.lockScroll(false);
    }

    const finish = () => {
      this.tween = null;
      if (this.isOpen) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (window.gsap) window.gsap.set(drawer, { clearProps: 'transform' });
      if (scrim) {
        scrim.hidden = true;
        scrim.style.opacity = '';
      }
    };

    if (this.reduced) {
      finish();
      return;
    }

    if (this.tween) this.tween.kill();
    const tl = window.gsap.timeline({ onComplete: finish });
    tl.to(drawer, { xPercent: 100, duration: this.dur(0.4), ease: 'power2.in' }, 0);
    if (scrim) tl.to(scrim, { opacity: 0, duration: this.dur(0.35), ease: 'power2.in' }, 0);
    this.tween = tl;
  }

  setExpanded(open) {
    document.querySelectorAll('[data-cart-open][aria-controls="VirsaayCartDrawer"]').forEach((el) => {
      el.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* -- Line changes ------------------------------------------------------ */

  step(button) {
    const item = button.closest('[data-cart-item]');
    const input = item && item.querySelector('[data-quantity-input]');
    if (!input) return;

    const step = parseInt(input.step, 10) || 1;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const current = parseInt(input.value, 10) || 0;
    const next = button.hasAttribute('data-quantity-plus') ? current + step : current - step;

    if (next > max) {
      this.showError(this.quantityError(max));
      return;
    }

    input.value = Math.max(0, next);
    this.scheduleChange(item, Math.max(0, next), 300);
  }

  quantityError(max) {
    return this.string('quantityError', '').replace('[quantity]', max);
  }

  // Collapse bursts of clicks on one line into a single request.
  scheduleChange(item, quantity, delay) {
    const key = item.dataset.key || item.dataset.line;
    clearTimeout(this.pending[key]);
    this.pending[key] = setTimeout(() => {
      delete this.pending[key];
      this.change(item, quantity);
    }, delay);
  }

  change(item, quantity) {
    if (!item) return Promise.resolve();
    const key = item.dataset.key;
    const line = item.dataset.line;
    item.setAttribute('aria-busy', 'true');
    this.queue = this.queue.then(() => this.runChange(item, key, line, quantity)).catch(() => {});
    return this.queue;
  }

  async runChange(item, key, line, quantity) {
    this.setLoading(true);
    try {
      const body = {
        quantity,
        sections: this.sectionIds().join(','),
        sections_url: window.location.pathname
      };
      if (key) body.id = key;
      else body.line = parseInt(line, 10);

      const res = await fetch(this.route('cartChange', '/cart/change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const state = await res.json().catch(() => ({}));
      if (!res.ok || state.status || state.errors) {
        throw new Error(state.description || state.message || this.string('cartError'));
      }

      if (quantity === 0 && item.isConnected) await this.collapse(item);
      this.render(state.sections, state.item_count);
      this.publish(state);
    } catch (error) {
      // Put the truth back on screen before explaining what went wrong.
      await this.refresh();
      this.showError(error.message || this.string('cartError'));
    } finally {
      this.setLoading(false);
    }
  }

  collapse(item) {
    if (this.reduced) return Promise.resolve();
    return new Promise((resolve) => {
      window.gsap.to(item, {
        height: 0,
        opacity: 0,
        paddingTop: 0,
        paddingBottom: 0,
        duration: this.dur(0.3),
        ease: 'power2.inOut',
        onComplete: resolve
      });
    });
  }

  async saveNote(note) {
    try {
      await fetch(this.route('cartUpdate', '/cart/update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
      const drawer = this.drawer;
      if (this.motion && drawer) this.motion.announce(drawer.dataset.stringNoteSaved || '');
    } catch (error) {
      this.showError(this.string('cartError'));
    }
  }

  /* -- Adding ------------------------------------------------------------ */

  async quickAdd(form, submitter) {
    const btn = submitter || form.querySelector('button[type="submit"]');
    const isIconButton = btn && btn.classList.contains('vcard__btn-cart');
    const label = btn ? btn.innerHTML : '';

    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }

    const data = new FormData(form);
    data.append('sections', this.sectionIds().join(','));
    data.append('sections_url', window.location.pathname);

    try {
      const res = await fetch(this.route('cartAdd', '/cart/add'), {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const state = await res.json().catch(() => ({}));
      if (!res.ok || state.status) {
        throw new Error(state.description || state.message || this.string('cartError'));
      }

      this.render(state.sections, null);
      this.publish(state);
      if (btn && !isIconButton) {
        btn.innerHTML = this.string('added', 'Added');
        setTimeout(() => {
          if (btn.isConnected) btn.innerHTML = label;
        }, 1400);
      }
      this.openDrawer();
    } catch (error) {
      this.showError(error.message);
      if (this.motion) this.motion.announce(error.message);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  }

  async buyNow(button) {
    const card = button.closest('[data-card]');
    const variantId = button.dataset.variantId || (card && card.querySelector('[data-card-variant-id]')?.value);
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('is-loading');
    const label = button.innerHTML;

    try {
      const res = await fetch(this.route('cartAdd', '/cart/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      });
      const state = await res.json().catch(() => ({}));
      if (!res.ok || state.status) {
        throw new Error(state.description || state.message || this.string('cartError'));
      }
      window.location.href = `${(this.theme.routes && this.theme.routes.root) || '/'}checkout`;
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.innerHTML = label;
      this.showError(error.message);
      if (this.motion) this.motion.announce(error.message);
    }
  }

  /* -- Rendering --------------------------------------------------------- */

  publish(state) {
    document.dispatchEvent(
      new CustomEvent('virsaay:cart:updated', {
        detail: { cart: state, sections: state.sections, source: 'virsaay-cart' }
      })
    );
  }

  parse(html, selector) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector);
  }

  render(sections, count) {
    if (!sections) {
      this.refresh();
      return;
    }

    let itemCount = typeof count === 'number' ? count : null;

    const freshDrawer = this.parse(sections['cart-drawer'], '#VirsaayCartDrawer');
    if (freshDrawer) {
      if (itemCount === null) itemCount = parseInt(freshDrawer.dataset.itemCount, 10);
      const drawer = this.drawer;
      if (drawer) this.swapDrawer(drawer, freshDrawer);
    }

    const page = this.page;
    const pageId = page && page.dataset.sectionId;
    if (page && pageId && sections[pageId]) {
      const freshPage = this.parse(sections[pageId], '[data-cart-page]');
      const inner = page.querySelector('[data-cart-page-inner]');
      const freshInner = freshPage && freshPage.querySelector('[data-cart-page-inner]');
      if (inner && freshInner) inner.innerHTML = freshInner.innerHTML;
      if (freshPage) {
        page.dataset.itemCount = freshPage.dataset.itemCount;
        if (itemCount === null) itemCount = parseInt(freshPage.dataset.itemCount, 10);
      }
    }

    if (itemCount === null) {
      const bubble = this.parse(sections['cart-icon-bubble'], '[data-cart-count]');
      if (bubble) itemCount = parseInt(bubble.textContent, 10);
    }

    if (Number.isFinite(itemCount)) this.updateCount(itemCount);
  }

  swapDrawer(drawer, fresh) {
    const scroller = drawer.querySelector('[data-cart-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const active = document.activeElement;
    const focusedId = active && drawer.contains(active) ? active.id : null;
    const noteOpen = !!drawer.querySelector('[data-cart-note-wrap][open]');
    const oldBar = drawer.querySelector('[data-shipping-bar]');
    const oldProgress = oldBar ? oldBar.dataset.progress : null;

    let flipState = null;
    if (this.isOpen && !this.reduced && window.Flip) {
      flipState = window.Flip.getState(drawer.querySelectorAll('[data-flip-id]'));
    }

    drawer.innerHTML = fresh.innerHTML;
    drawer.dataset.itemCount = fresh.dataset.itemCount;

    const nextScroller = drawer.querySelector('[data-cart-scroll]');
    if (nextScroller) nextScroller.scrollTop = scrollTop;

    if (noteOpen) {
      const note = drawer.querySelector('[data-cart-note-wrap]');
      if (note) note.open = true;
    }

    // The focused control was replaced with the markup; put focus back on its
    // successor (same id), or on the close button if that line is gone.
    if (this.isOpen && active && !active.isConnected) {
      const target = (focusedId && document.getElementById(focusedId)) || drawer.querySelector('[data-cart-close]');
      if (target && drawer.contains(target)) target.focus({ preventScroll: true });
    }

    if (flipState) {
      window.Flip.from(flipState, {
        targets: drawer.querySelectorAll('[data-flip-id]'),
        duration: this.dur(0.4),
        ease: this.motion.ease,
        onEnter: (elements) => window.gsap.fromTo(elements, { opacity: 0 }, { opacity: 1, duration: this.dur(0.3) })
      });
    }

    // Let the shipping bar travel from its old length to the new one.
    const bar = drawer.querySelector('[data-shipping-bar]');
    const fill = bar && bar.querySelector('[data-shipping-fill]');
    if (fill && oldProgress !== null && oldProgress !== bar.dataset.progress && !this.reduced) {
      const next = bar.dataset.progress;
      fill.style.setProperty('--v-progress', oldProgress);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => fill.style.setProperty('--v-progress', next));
      });
    }
  }

  async refresh() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`, {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return;
      const sections = await res.json();
      if (sections) this.render(sections, null);
    } catch (error) {
      // Offline or blocked — the markup on screen is the best we have.
    }
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const changed = el.textContent.trim() !== String(count);
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      if (changed && count > 0 && !this.reduced && el.classList.contains('virsaay-header__cart-badge')) {
        window.gsap.fromTo(el, { scale: 1.4 }, { scale: 1, duration: 0.35, ease: 'back.out(2)', clearProps: 'transform' });
      }
    });
  }

  setLoading(on) {
    [this.drawer, this.page].forEach((root) => {
      if (!root) return;
      root.classList.toggle('is-loading', on);
      root.querySelectorAll('[data-cart-checkout]').forEach((btn) => {
        if (on) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      });
    });
  }

  showError(message) {
    if (!message) return;
    const targets = [this.drawer, this.page]
      .filter(Boolean)
      .map((root) => root.querySelector('[data-cart-error]'))
      .filter(Boolean);

    targets.forEach((el) => {
      el.textContent = message;
      el.hidden = false;
    });
    if (this.motion) this.motion.announce(message);

    clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => {
      targets.forEach((el) => {
        if (el.isConnected) el.hidden = true;
      });
    }, 6000);
  }

  /* -- Editorial card media --------------------------------------------- */

  selectDetailThumb(detailThumb) {
    const strip = detailThumb.closest('[data-card-detail-strip]');
    if (strip) {
      if (detailThumb.classList.contains('is-active')) return;
      strip.querySelectorAll('[data-detail-thumb]').forEach((el) => el.classList.remove('is-active'));
      detailThumb.classList.add('is-active');
    }
    const card = detailThumb.closest('[data-card]');
    const src = detailThumb.dataset.src;
    if (card && src) {
      const alt = detailThumb.querySelector('img')?.alt || '';
      this.animateCardImageSlide(card, src, alt);
    }
  }

  handleThumbnailHover(e) {
    const detailThumb = e.target.closest && e.target.closest('[data-detail-thumb]');
    clearTimeout(this._thumbHoverTimer);
    if (!detailThumb || detailThumb.classList.contains('is-active')) return;

    // Hover intent: gliding across the strip must not fire a swap per thumb.
    this._thumbHoverTimer = setTimeout(() => this.selectDetailThumb(detailThumb), 220);
  }

  /**
   * Swap the card's main image with one slow crossfade: the new image is
   * decoded first (no blank flash), fades in over the old one and settles
   * from a slight zoom. A request made mid-swap is queued, not dropped, so the
   * card always ends on the last thumbnail chosen.
   */
  animateCardImageSlide(card, src, altText) {
    const mediaEl = card.querySelector('[data-card-media]');
    if (!mediaEl || !src) return;

    if (card._isAnimatingSlide) {
      card._pendingSlide = { src, altText };
      return;
    }

    const currentFrame = mediaEl.querySelector('.vcard__image-frame--primary') || mediaEl.querySelector('.vcard__image-frame');
    const currentImg = currentFrame ? currentFrame.querySelector('.vcard__image') : null;
    const shownPath = card._slideSrc || (currentImg ? (currentImg.currentSrc || currentImg.src) : '');
    if (shownPath.split('?')[0] === src.split('?')[0]) return;
    card._isAnimatingSlide = true;
    card._slideSrc = src;

    const incomingFrame = document.createElement('div');
    incomingFrame.className = 'vcard__image-frame vcard__image-frame--incoming';
    incomingFrame.style.zIndex = '3';
    incomingFrame.style.opacity = '0';

    const incomingImg = document.createElement('img');
    incomingImg.className = 'vcard__image';
    incomingImg.alt = altText || (currentImg ? currentImg.alt : '');
    incomingImg.decoding = 'async';
    incomingImg.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;transform:scale(1.045);';
    incomingImg.src = src;
    incomingFrame.appendChild(incomingImg);

    const settle = () => {
      if (currentFrame && currentFrame.parentNode) currentFrame.remove();
      incomingFrame.className = 'vcard__image-frame vcard__image-frame--primary';
      incomingFrame.removeAttribute('style');
      incomingImg.className = 'vcard__image vcard__image--primary';
      incomingImg.removeAttribute('style');
      card._isAnimatingSlide = false;
      const next = card._pendingSlide;
      card._pendingSlide = null;
      if (next && next.src !== src) this.animateCardImageSlide(card, next.src, next.altText);
    };

    const run = () => {
      mediaEl.appendChild(incomingFrame);
      // The hover photo stands down only now, as the new image fades in over
      // it; otherwise the pointer resting on the card keeps it on top.
      card.classList.add('is-detail-picked');
      if (this.reduced) {
        settle();
        return;
      }
      if (window.gsap) {
        const gsap = window.gsap;
        const tl = gsap.timeline({ onComplete: settle });
        tl.to(incomingFrame, { opacity: 1, duration: this.dur(0.75), ease: 'power1.inOut' }, 0);
        tl.to(incomingImg, { scale: 1, duration: this.dur(1.1), ease: 'power3.out' }, 0);
      } else {
        requestAnimationFrame(() => {
          incomingFrame.style.transition = 'opacity 0.75s cubic-bezier(0.45, 0, 0.55, 1)';
          incomingImg.style.transition = 'transform 1.1s cubic-bezier(0.22, 1, 0.36, 1)';
          incomingFrame.style.opacity = '1';
          incomingImg.style.transform = 'scale(1)';
          setTimeout(settle, 1100);
        });
      }
    };

    // Wait for the pixels (capped) so the fade never starts on an empty frame.
    const ready = incomingImg.decode ? incomingImg.decode().catch(() => {}) : Promise.resolve();
    Promise.race([ready, new Promise((r) => setTimeout(r, 1200))]).then(run);
  }
}

function bootCart() {
  if (!window.VirsaayCartInstance) {
    window.VirsaayCartInstance = new VirsaayCart();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootCart);
} else {
  bootCart();
}
